import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { hireNotifications } from '@/api/hr';
import { orgs } from '@/api/catalogs';

/**
 * Друкована форма «Повідомлення про прийняття працівника (домашнього
 * працівника) на роботу / укладення гіг-контракту» — додаток 1 до постанови
 * КМУ від 17.06.2015 №413 (в редакції постанови №184 від 21.02.2025).
 * Макет повторює офіційну форму (шапка 1-3, таблиця 4-12, футер 13-15, примітки).
 */

const dt = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString('uk-UA') : '';

const cellB: React.CSSProperties = {
  border: '1px solid #000', padding: '2pt 3pt', verticalAlign: 'top',
};

export default function HireNotificationPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['hire-notification', id ?? ''],
    queryFn: () => hireNotifications.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);
  const isInitial = d.notification_type === 'INITIAL';

  return (
    <div className="print-page" style={{
      padding: '10mm', fontFamily: 'Times, serif', fontSize: '9pt',
      maxWidth: '287mm', margin: '0 auto', background: 'white', lineHeight: 1.35,
    }}>
      <div className="print-hide" style={{
        position: 'sticky', top: 0, background: '#f1f5f9',
        padding: '0.5rem 1rem', marginBottom: '1rem', borderRadius: 6,
        display: 'flex', gap: '0.5rem', alignItems: 'center',
      }}>
        <strong>Повідомлення про прийняття працівника на роботу (КМУ №413)</strong>
        <button className="primary" onClick={() => window.print()} style={{ marginLeft: 'auto' }}>
          🖨 Друкувати
        </button>
      </div>

      <div style={{ textAlign: 'right', fontSize: '8pt' }}>
        <div>Додаток 1</div>
        <div>до постанови Кабінету Міністрів України</div>
        <div>від 17 червня 2015 р. № 413</div>
        <div>(в редакції постанови Кабінету Міністрів України</div>
        <div>від 21 лютого 2025 р. № 184)</div>
      </div>

      <div style={{ marginTop: '4pt' }}>№ аркуша повідомлення 1</div>

      <div style={{ textAlign: 'center', fontWeight: 'bold', margin: '8pt 0', fontSize: '11pt' }}>
        ПОВІДОМЛЕННЯ<br />
        <span style={{ fontSize: '9pt', fontWeight: 'normal' }}>
          про прийняття працівника (домашнього працівника) на роботу/укладення гіг-контракту
        </span>
      </div>

      {/* 1-3: тип, ЄДРПОУ, найменування */}
      <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '6pt' }}>
        <tbody>
          <tr>
            <td style={{ ...cellB, width: '4%' }}>1.</td>
            <td style={{ ...cellB, width: '46%' }}>Тип повідомлення</td>
            <td style={{ ...cellB, width: '25%', textAlign: 'center' }}>
              {isInitial ? 'X' : ' '} початкове
            </td>
            <td style={{ ...cellB, width: '25%', textAlign: 'center' }}>
              {!isInitial ? 'X' : ' '} скасовуюче
            </td>
          </tr>
          <tr>
            <td style={cellB}>2.</td>
            <td style={cellB}>
              Код згідно з ЄДРПОУ/реєстраційний номер облікової картки платника
              податків або серія (за наявності) та номер паспорта*
            </td>
            <td style={{ ...cellB, textAlign: 'center' }} colSpan={2}>
              {org?.edrpou ?? org?.inn ?? ''}
            </td>
          </tr>
          <tr>
            <td style={cellB}>3.</td>
            <td style={cellB}>
              Найменування/прізвище, ім'я, по батькові (за наявності) страхувальника
              або фізичної особи, яка уклала трудовий договір з домашнім працівником
            </td>
            <td style={{ ...cellB, textAlign: 'center' }} colSpan={2}>
              {org?.full_name ?? org?.name ?? ''}
            </td>
          </tr>
        </tbody>
      </table>

      {/* 4-12: таблиця осіб */}
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '8pt' }}>
        <thead>
          <tr style={{ textAlign: 'center' }}>
            <th style={cellB}>4. Порядковий номер</th>
            <th style={cellB}>5. Категорія особи**</th>
            <th style={cellB}>
              6. Реєстраційний номер облікової картки платника податків або серія
              (за наявності) та номер паспорта*
            </th>
            <th style={cellB}>
              7. Унікальний номер запису в Єдиному державному демографічному
              реєстрі (за наявності)
            </th>
            <th style={cellB}>
              8. Прізвище, ім'я, по батькові (за наявності) застрахованої особи
              або домашнього працівника
            </th>
            <th style={cellB}>9. Грома­дянство***</th>
            <th style={cellB}>
              10. Номер наказу або розпорядження про прийняття на роботу****
            </th>
            <th style={cellB}>
              11. Дата видання наказу або розпорядження про прийняття на
              роботу/дата укладення гіг-контракту/дата укладення
              цивільно-правового договору або трудового договору з домашнім
              працівником
            </th>
            <th style={cellB}>
              12. Дата початку роботи/початку виконання робіт (надання послуг)
              гіг-спеціалістом/особою на підставі цивільно-правового договору
            </th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => (
            <tr key={it.id ?? i} style={{ textAlign: 'center' }}>
              <td style={cellB}>{i + 1}</td>
              <td style={cellB}>{it.category}</td>
              <td style={cellB}>{it.tax_number ?? ''}</td>
              <td style={cellB}>{it.unzr ?? ''}</td>
              <td style={{ ...cellB, textAlign: 'left' }}>
                {it.last_name} {it.first_name}{it.middle_name ? ` ${it.middle_name}` : ''}
              </td>
              <td style={cellB}>{it.citizenship}</td>
              <td style={cellB}>{it.order_number ?? ''}</td>
              <td style={cellB}>{dt(it.order_date)}</td>
              <td style={cellB}>{dt(it.start_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 13-15: дата формування + підписанти */}
      <div style={{ marginTop: '8pt' }}>
        13. Дата формування повідомлення у страхувальника або фізичної особи,
        яка уклала трудовий договір з домашнім працівником:{' '}
        <u>{dt(d.fill_date ?? d.document_date)}</u>
      </div>

      <table style={{ width: '100%', marginTop: '10pt', fontSize: '8.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%', verticalAlign: 'bottom' }}>
              14. Керівник (уповноважена особа) або фізична особа, яка уклала
              трудовий договір з домашнім працівником
              <div style={{ borderBottom: '1px solid #000', textAlign: 'center', marginTop: '10pt' }}>
                {d.kbos_inn ?? ''}
              </div>
              <div style={{ fontSize: '7.5pt', textAlign: 'center' }}>
                (реєстраційний номер облікової картки платника податків або серія
                (за наявності) та номер паспорта*)
              </div>
            </td>
            <td style={{ width: '5%' }} />
            <td style={{ width: '40%', verticalAlign: 'bottom' }}>
              <div style={{ borderBottom: '1px solid #000', textAlign: 'center', marginTop: '10pt' }}>
                {d.bos_name ?? ''}
              </div>
              <div style={{ fontSize: '7.5pt', textAlign: 'center' }}>
                (власне ім'я та прізвище)
              </div>
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: 'bottom', paddingTop: '8pt' }}>
              15. Головний бухгалтер (особа, відповідальна за ведення
              бухгалтерського обліку)*****
              <div style={{ borderBottom: '1px solid #000', textAlign: 'center', marginTop: '10pt' }}>
                {d.kbuh_inn ?? ''}
              </div>
              <div style={{ fontSize: '7.5pt', textAlign: 'center' }}>
                (реєстраційний номер облікової картки платника податків або серія
                (за наявності) та номер паспорта*)
              </div>
            </td>
            <td />
            <td style={{ verticalAlign: 'bottom', paddingTop: '8pt' }}>
              <div style={{ borderBottom: '1px solid #000', textAlign: 'center', marginTop: '10pt' }}>
                {d.buh_name ?? ''}
              </div>
              <div style={{ fontSize: '7.5pt', textAlign: 'center' }}>
                (власне ім'я та прізвище)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: '10pt', fontSize: '7.5pt' }}>
        <div>__________</div>
        <div>
          * Серія (за наявності) та номер паспорта зазначаються для фізичних осіб,
          які через свої релігійні переконання відмовляються від прийняття
          реєстраційного номера облікової картки платника податків та офіційно
          повідомили про це відповідному контролюючому органу і мають відмітку у паспорті.
        </div>
        <div>
          ** Категорія особи: 1 — наймані працівники за основним місцем роботи;
          2 — працівники за сумісництвом; 3 — гіг-спеціалісти за гіг-контрактом;
          4 — особи на підставі цивільно-правових договорів; 5 — домашні працівники.
        </div>
        <div>*** Громадянство: 0 — іноземець або особа без громадянства; 1 — громадянин України.</div>
        <div>
          **** Не зазначається у разі укладення гіг-контракту/цивільно-правового
          договору/трудового договору з домашнім працівником.
        </div>
        <div>***** Не зазначається у разі укладення трудового договору з домашнім працівником.</div>
      </div>
    </div>
  );
}
